// Supabase client configuration will go here

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {})

/**
 * Gets the access token from Supabase
 * @returns {Promise<string>} The access token
 * @throws {Error} If the session is not found
 */
async function getToken() {
  const {
    data: { session },
    error: sessionError
  } = await supabase.auth.getSession()
  if (sessionError) throw sessionError
  if (!session) throw new Error('No active session')
  return session.access_token
}

/**
 * Calls a Supabase function
 * @param {Object} options - The options for the function call
 * @param {string} options.functionName - The name of the function to call
 * @param {string} [options.method="POST"] - The HTTP method to use for the request
 * @param {Object} [options.headers={}] - The headers to include in the request
 * @param {Object} [options.body=null] - The body of the request
 * @returns {Promise<Object>} The response from the function call
 * @throws {Error} If the function call fails
 */
/**
 * Calls a Supabase function with streaming response support
 * @param {Object} options - The options for the function call
 * @param {string} options.functionName - The name of the function to call
 * @param {string} [options.method="POST"] - The HTTP method to use for the request
 * @param {Object} [options.headers={}] - The headers to include in the request
 * @param {Object} [options.body=null] - The body of the request
 * @returns {Promise<Response>} The raw response object for streaming
 * @throws {Error} If the function call fails
 */
export async function callSupabaseFunctionStream({
  functionName,
  method = 'POST',
  headers = {},
  body = null,
  hasSession = true
}) {
  // Set up request headers
  let requestHeaders = {
    ...headers,
    Authorization: hasSession ? `Bearer ${await getToken()}` : `Bearer ${supabaseAnonKey}`,
    'Content-Type': 'application/json'
  }
  const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`

  // Make the function call and return the raw response for streaming
  try {
    const response = await fetch(functionUrl, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : null
    })
    
    if (!response.ok) {
      // For error responses, we'll still parse them to get the error message
      const contentType = response.headers.get('content-type')
      let errorData
      
      if (contentType?.includes('application/json')) {
        errorData = await response.json()
      } else if (contentType?.includes('text/')) {
        errorData = await response.text()
      } else {
        errorData = 'Unknown error occurred'
      }
      
      let errorMessage = `Error ${response.status}`
      if (response.status === 400) {
        errorMessage = errorData?.error || errorData?.message || 'Bad Request'
      } else if (response.status === 500) {
        errorMessage = 'Internal Server Error'
      } else if (typeof errorData === 'string') {
        errorMessage = errorData
      }
      
      throw new Error(errorMessage)
    }
    
    return response
  } catch (error) {
    if (error instanceof TypeError && error.status) {
      throw error
    }
    console.error('Network or Fetch Error:', error)
    throw new Error(error.message)
  }
}

/**
 * Calls a Supabase function
 * @param {Object} options - The options for the function call
 * @param {string} options.functionName - The name of the function to call
 * @param {string} [options.method="POST"] - The HTTP method to use for the request
 * @param {Object} [options.headers={}] - The headers to include in the request
 * @param {Object} [options.body=null] - The body of the request
 * @returns {Promise<Object>} The response from the function call
 * @throws {Error} If the function call fails
 */
export async function callSupabaseFunction({
  functionName,
  method = 'POST',
  headers = {},
  body = null,
  hasSession = true
}) {
  // Set up request headers
  let requestHeaders = {
    ...headers,
    Authorization: hasSession ? `Bearer ${await getToken()}` : `Bearer ${supabaseAnonKey}`,
    'Content-Type': 'application/json'
  }
  const functionUrl = `${supabaseUrl}/functions/v1/${functionName}`

  // Make the function call
  try {
    const response = await fetch(functionUrl, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : null
    })

    // Determine how to parse the response
    const contentType = response.headers.get('content-type')
    let responseData
    if (contentType?.includes('application/json')) {
      responseData = await response.json()
    } else if (contentType?.includes('text/')) {
      responseData = await response.text()
    } else {
      responseData = await response.blob() // Handle binary data if needed
    }
    if (!response.ok) {
      let errorMessage = `Error ${response.status}`

      if (response.status === 400) {
        errorMessage = responseData?.error || responseData?.message || 'Bad Request'
      } else if (response.status === 500) {
        errorMessage = 'Internal Server Error'
      } else if (typeof responseData === 'string') {
        errorMessage = responseData // For plain text responses
      }

      throw new Error(errorMessage)
    }
    return responseData
  } catch (error) {
    if (error instanceof TypeError && error.status) {
      throw error
    }
    console.error('Network or Fetch Error:', error)
    throw new Error(error.message)
  }
}
